import { Calendar, Clock, Dumbbell, Link as LinkIcon, Plus, User as UserIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import type { Booking } from '@coachapp/shared';
import type { BookingFormDraft, BookingFormLocationState } from '../lib/form-return';
import {
  clearBookingFormDraft,
  draftFromLocationState,
  mergeBookingDrafts,
  peekBookingFormDraft,
  saveBookingFormDraft,
} from '../lib/form-return';
import CompleteBookingPrompt, {
  type CompleteBookingRequest,
} from '../components/CompleteBookingPrompt';
import DatePicker from '../components/DatePicker';
import { FormPageLayout } from '../components/layout/FormPageLayout';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import { filterClientPlans } from '../lib/training-plan-utils';
import {
  getLastBookingForClient,
  resolveLastSessionDefaults,
} from '../lib/client-booking-defaults';
import { isArchivedClient } from '../lib/client-status';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from '../lib/datetime-local';
import {
  useCreateBookingMutation,
  useGetBookingsQuery,
  useGetClientsQuery,
  useGetTrainingPlansQuery,
  useDeleteBookingMutation,
  useUpdateBookingMutation,
} from '../store/coachApi';

interface BookingFormContentProps {
  initialData?: Booking;
  defaultStartsAt?: string;
  draft?: BookingFormDraft;
  onClose: () => void;
  onCreated: (date: string) => void;
}

const BookingFormContent = ({
  initialData,
  defaultStartsAt,
  draft,
  onClose,
  onCreated,
}: BookingFormContentProps) => {
  const { t } = useTranslation();
  const navigate = useTransitionNavigate();
  const [error, setError] = useState('');
  const [completeRequest, setCompleteRequest] = useState<CompleteBookingRequest | null>(null);
  const { data: clients = [], isFetching: fetchingClients, error: clientsError } = useGetClientsQuery();
  const { data: allBookings = [], isFetching: fetchingBookings } = useGetBookingsQuery();
  const { data: allPlans = [], isFetching: fetchingPlans } = useGetTrainingPlansQuery();
  const clientsLoading = fetchingClients && clients.length === 0;
  const bookingsLoading = fetchingBookings && allBookings.length === 0;
  const plansLoading = fetchingPlans && allPlans.length === 0;
  const [createBooking, { isLoading: creating }] = useCreateBookingMutation();
  const [updateBooking, { isLoading: updating }] = useUpdateBookingMutation();
  const [deleteBooking, { isLoading: deleting }] = useDeleteBookingMutation();
  const loading = creating || updating;

  const isActiveBooking =
    initialData?.status === 'PENDING' || initialData?.status === 'CONFIRMED';

  const [formData, setFormData] = useState({
    clientId: draft?.clientId || initialData?.clientId || '',
    trainingPlanId: draft?.trainingPlanId || initialData?.trainingPlanId || '',
    type: draft?.type || initialData?.type || 'GYM',
    startsAt: draft?.startsAt
      ? draft.startsAt.slice(0, 16)
      : initialData?.startsAt
        ? toDatetimeLocalValue(initialData.startsAt)
        : defaultStartsAt
          ? defaultStartsAt.slice(0, 16)
          : toDatetimeLocalValue(new Date()),
    durationMinutes: draft?.durationMinutes || initialData?.durationMinutes || 60,
    link: draft?.link || initialData?.link || '',
    status: initialData?.status || 'PENDING',
  });

  const fetchError = clientsError
    ? getApiErrorMessage(clientsError, 'Не удалось загрузить список клиентов')
    : null;

  const selectableClients = useMemo(
    () => clients.filter((client) => !isArchivedClient(client)),
    [clients],
  );

  const resolvedClientId = useMemo(() => {
    if (!formData.clientId || clientsLoading) return formData.clientId;
    return selectableClients.some((client) => client.id === formData.clientId)
      ? formData.clientId
      : '';
  }, [formData.clientId, selectableClients, clientsLoading]);

  const clientPlans = useMemo(
    () =>
      resolvedClientId
        ? filterClientPlans(allPlans, resolvedClientId, 'ACTIVE')
        : [],
    [allPlans, resolvedClientId],
  );

  const resolvedTrainingPlanId = useMemo(() => {
    if (!resolvedClientId) return '';
    if (!formData.trainingPlanId || plansLoading) return formData.trainingPlanId;
    return clientPlans.some((plan) => plan.id === formData.trainingPlanId)
      ? formData.trainingPlanId
      : '';
  }, [formData.trainingPlanId, clientPlans, plansLoading, resolvedClientId]);

  const [appliedClientId, setAppliedClientId] = useState<string | null>(null);
  const pendingClientDefaults =
    !initialData &&
    resolvedClientId &&
    !bookingsLoading &&
    !plansLoading &&
    appliedClientId !== resolvedClientId
      ? resolvedClientId
      : null;

  if (pendingClientDefaults) {
    const hasDraftOverrides = Boolean(draft?.trainingPlanId || draft?.durationMinutes);
    setAppliedClientId(pendingClientDefaults);
    if (!hasDraftOverrides) {
      const planIds = new Set(
        filterClientPlans(allPlans, pendingClientDefaults, 'ACTIVE').map((plan) => plan.id),
      );
      const lastBooking = getLastBookingForClient(allBookings, pendingClientDefaults);
      const defaults = resolveLastSessionDefaults(lastBooking, planIds);
      if (defaults) {
        setFormData((prev) => ({
          ...prev,
          trainingPlanId: defaults.trainingPlanId || prev.trainingPlanId,
          durationMinutes: defaults.durationMinutes,
        }));
      }
    }
  }

  const handleClientChange = (clientId: string) => {
    setAppliedClientId(clientId);

    setFormData((prev) => {
      const plansForClient = clientId
        ? filterClientPlans(allPlans, clientId, 'ACTIVE')
        : [];
      const planIds = new Set(plansForClient.map((plan) => plan.id));

      let trainingPlanId = '';
      let durationMinutes = prev.durationMinutes;

      if (!initialData && clientId) {
        const lastBooking = getLastBookingForClient(allBookings, clientId);
        const defaults = resolveLastSessionDefaults(lastBooking, planIds);
        if (defaults) {
          trainingPlanId = defaults.trainingPlanId;
          durationMinutes = defaults.durationMinutes;
        }
      } else {
        const keepsPlan =
          prev.trainingPlanId &&
          plansForClient.some((plan) => plan.id === prev.trainingPlanId);
        trainingPlanId = keepsPlan ? prev.trainingPlanId : '';
      }

      return {
        ...prev,
        clientId,
        trainingPlanId,
        durationMinutes,
      };
    });
  };

  const buildPayload = () => ({
    clientId: resolvedClientId,
    type: formData.type,
    startsAt: fromDatetimeLocalValue(formData.startsAt),
    durationMinutes: formData.durationMinutes,
    link: formData.link || undefined,
    status: formData.status,
    trainingPlanId: resolvedTrainingPlanId || null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (
        initialData &&
        formData.status === 'COMPLETED' &&
        initialData.status !== 'COMPLETED'
      ) {
        setCompleteRequest({
          bookingId: initialData.id,
          clientId: resolvedClientId,
          clientName: clients.find((c) => c.id === resolvedClientId)?.name,
          updateData: buildPayload(),
        });
        return;
      }

      if (initialData) {
        await updateBooking({ id: initialData.id, data: buildPayload() }).unwrap();
        clearBookingFormDraft();
        onClose();
      } else {
        await createBooking(buildPayload()).unwrap();
        clearBookingFormDraft();
        onCreated(formData.startsAt.slice(0, 10));
      }
    } catch (err) {
      setError(
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : getApiErrorMessage(err, 'Ошибка при сохранении'),
      );
    }
  };

  const pageTitle = initialData ? t('Редактировать тренировку') : t('Новая тренировка');
  const selectedClientName = selectableClients.find((c) => c.id === resolvedClientId)?.name;

  const cancelBooking = async () => {
    if (!initialData) return;
    setError('');
    try {
      await updateBooking({ id: initialData.id, data: { status: 'CANCELLED' } }).unwrap();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ошибка при сохранении'));
    }
  };

  const removeBooking = async () => {
    if (!initialData) return;
    if (!window.confirm(t('Вы уверены, что хотите удалить эту тренировку?'))) return;
    setError('');
    try {
      await deleteBooking(initialData.id).unwrap();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ошибка при сохранении'));
    }
  };

  const openCreatePlan = () => {
    if (!resolvedClientId) return;

    const returnTo = initialData ? `/bookings/${initialData.id}/edit` : '/bookings/new';
    const returnState: BookingFormDraft = {
      clientId: resolvedClientId,
      trainingPlanId: resolvedTrainingPlanId || undefined,
      startsAt: formData.startsAt,
      type: formData.type as Booking['type'],
      durationMinutes: formData.durationMinutes,
      link: formData.link || undefined,
    };

    saveBookingFormDraft(returnState);

    navigate(`/clients/${resolvedClientId}/plans/new`, {
      state: {
        returnTo,
        returnState,
        ...(initialData ? { booking: initialData } : {}),
      },
    });
  };

  return (
    <>
      <FormPageLayout title={pageTitle} onBack={onClose}>
        <form onSubmit={handleSubmit} className="space-y-5">
          {(error || fetchError) && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-600 rounded-2xl text-center text-sm font-medium">
              {error || fetchError}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="section-label ml-1 flex items-center gap-1.5">
              <UserIcon size={12} /> {t('Клиент')}
            </label>
            <select
              className="input-field font-medium appearance-none cursor-pointer"
              value={resolvedClientId}
              onChange={(e) => handleClientChange(e.target.value)}
              required
              disabled={clientsLoading}
            >
              <option value="" disabled>
                {t('Выберите клиента')}
              </option>
              {selectableClients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="section-label ml-1 flex items-center gap-1.5">
              <Dumbbell size={12} /> {t('План тренировки')}
            </label>
            <p className="text-xs text-slate-500 ml-1">
              {t('План — программа упражнений для этой сессии')}
            </p>
            <select
              className="input-field font-medium appearance-none cursor-pointer"
              value={resolvedTrainingPlanId}
              onChange={(e) => setFormData({ ...formData, trainingPlanId: e.target.value })}
              disabled={!resolvedClientId || plansLoading}
            >
              <option value="">{t('Без плана')}</option>
              {clientPlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
            {resolvedClientId && !plansLoading && clientPlans.length === 0 && (
              <button
                type="button"
                onClick={openCreatePlan}
                className="btn-secondary w-full min-h-11 text-sm mt-1"
              >
                <Plus size={16} />
                {t('Создать план для {{name}}', { name: selectedClientName ?? t('Клиент') })}
              </button>
            )}
            {resolvedClientId && !plansLoading && clientPlans.length > 0 && (
              <button
                type="button"
                onClick={openCreatePlan}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-dark ml-1 min-h-11"
              >
                <Plus size={14} />
                {t('Новый план')}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="section-label ml-1">
                {t('Тип тренировки')}
              </label>
              <select
                className="input-field font-medium appearance-none cursor-pointer"
                value={formData.type}
                onChange={(e) => {
                  const newType = e.target.value as Booking['type'];
                  setFormData({
                    ...formData,
                    type: newType,
                    link: newType === 'ONLINE' ? formData.link : '',
                  });
                }}
                required
              >
                <option value="GYM">{t('В зале')}</option>
                <option value="ONLINE">{t('Онлайн')}</option>
                <option value="SELF">{t('Самостоятельно')}</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="section-label ml-1 flex items-center gap-1.5">
                <Clock size={12} /> {t('Длительность (мин)')}
              </label>
              <input
                type="number"
                className="input-field font-medium"
                value={formData.durationMinutes}
                onChange={(e) =>
                  setFormData({ ...formData, durationMinutes: parseInt(e.target.value, 10) })
                }
                min="1"
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="section-label ml-1 flex items-center gap-1.5">
              <Calendar size={12} /> {t('Дата и время начала')}
            </label>
            <DatePicker
              mode="datetime"
              value={formData.startsAt}
              onChange={(startsAt) => setFormData({ ...formData, startsAt })}
              required
            />
          </div>

          {formData.type === 'ONLINE' && (
            <div className="space-y-1.5">
              <label className="section-label ml-1 flex items-center gap-1.5">
                <LinkIcon size={12} /> {t('Ссылка (для онлайн)')}
              </label>
              <input
                type="url"
                className="input-field"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                placeholder="https://zoom.us/j/..."
              />
            </div>
          )}

          {initialData && (
            <div className="space-y-1.5">
              <label className="section-label ml-1">
                {t('Статус')}
              </label>
              <select
                className="input-field font-medium appearance-none cursor-pointer"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as Booking['status'] })}
                required
              >
                <option value="PENDING">{t('status.PENDING')}</option>
                <option value="CONFIRMED">{t('status.CONFIRMED')}</option>
                <option value="COMPLETED">{t('status.COMPLETED')}</option>
                <option value="CANCELLED">{t('status.CANCELLED')}</option>
              </select>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
            <button type="button" className="btn-secondary flex-1 min-h-12" onClick={onClose}>
              {t('Отмена')}
            </button>
            <button type="submit" className="btn-primary flex-1 sm:flex-[2] min-h-12" disabled={loading}>
              {loading ? t('Сохранение...') : initialData ? t('Сохранить изменения') : t('Создать тренировку')}
            </button>
          </div>

          {isActiveBooking && (
            <div className="flex flex-col gap-3 pt-2">
              <button
                type="button"
                className="btn-secondary text-rose-700"
                onClick={() => void cancelBooking()}
                disabled={updating || deleting}
              >
                {t('Отменить тренировку')}
              </button>
              <button
                type="button"
                className="btn-secondary text-rose-700"
                onClick={() => void removeBooking()}
                disabled={deleting}
              >
                {t('Удалить')}
              </button>
            </div>
          )}
        </form>
      </FormPageLayout>

      <CompleteBookingPrompt
        request={completeRequest}
        onClose={() => setCompleteRequest(null)}
        onSuccess={onClose}
      />
    </>
  );
};

const BookingFormRoute = () => {
  const navigate = useTransitionNavigate();
  const location = useLocation();
  const { bookingId } = useParams();
  const { t } = useTranslation();
  const locationState = location.state as BookingFormLocationState | null;
  const { data: bookings, isLoading: bookingsLoading } = useGetBookingsQuery(undefined, {
    skip: !bookingId || !!locationState?.booking,
  });
  const initialData =
    locationState?.booking ??
    (bookingId ? bookings?.find((item) => item.id === bookingId) : undefined);
  const defaultStartsAt = !bookingId ? locationState?.startsAt : undefined;
  const draft = mergeBookingDrafts(
    peekBookingFormDraft(),
    draftFromLocationState(locationState),
  );
  const detailLoading = !!bookingId && !locationState?.booking && bookingsLoading;
  const close = () => {
    clearBookingFormDraft();
    navigate(-1);
  };
  const afterCreate = (date: string) => {
    clearBookingFormDraft();
    navigate(date ? `/?date=${date}` : '/', { replace: true });
  };
  const pageTitle = initialData ? t('Редактировать тренировку') : t('Новая тренировка');

  if (detailLoading) {
    return <FormPageLayout title={pageTitle} onBack={close} loading />;
  }

  return (
    <BookingFormContent
      key={initialData?.id ?? 'new'}
      initialData={initialData}
      defaultStartsAt={defaultStartsAt}
      draft={draft}
      onClose={close}
      onCreated={afterCreate}
    />
  );
};

export default BookingFormRoute;
